/**
 * Icelandic date formatting that does NOT depend on the runtime's Intl/ICU
 * locale data. `Intl.DateTimeFormat('is-IS', …)` silently falls back to English
 * ("July 16, 2026") in environments whose ICU build lacks Icelandic, so we format
 * the parts ourselves to guarantee Icelandic output everywhere.
 */

import { getLocale, type Locale } from '@/i18n/locale'

const MONTHS_LONG: Record<Locale, string[]> = {
  is: [
    'janúar', 'febrúar', 'mars', 'apríl', 'maí', 'júní',
    'júlí', 'ágúst', 'september', 'október', 'nóvember', 'desember',
  ],
  en: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
  pl: [
    'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
    'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia',
  ],
}

const MONTHS_SHORT: Record<Locale, string[]> = {
  is: [
    'jan.', 'feb.', 'mar.', 'apr.', 'maí', 'jún.',
    'júl.', 'ágú.', 'sep.', 'okt.', 'nóv.', 'des.',
  ],
  en: [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ],
  pl: [
    'sty', 'lut', 'mar', 'kwi', 'maj', 'cze',
    'lip', 'sie', 'wrz', 'paź', 'lis', 'gru',
  ],
}

const MONTH_LABELS: Record<Locale, { short: string[]; long: string[] }> = {
  is: {
    short: ['Jan', 'Feb', 'Mars', 'Apríl', 'Maí', 'Júní', 'Júlí', 'Ágú', 'Sept', 'Okt', 'Nóv', 'Des'],
    long: [
      'Janúar', 'Febrúar', 'Mars', 'Apríl', 'Maí', 'Júní',
      'Júlí', 'Ágúst', 'September', 'Október', 'Nóvember', 'Desember',
    ],
  },
  en: {
    short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    long: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ],
  },
  pl: {
    short: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
    long: [
      'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
      'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
    ],
  },
}

export function monthNames(form: 'short' | 'long' = 'short'): string[] {
  return MONTH_LABELS[getLocale()][form]
}

export function monthName(monthIndex: number, form: 'short' | 'long' = 'short'): string {
  return MONTH_LABELS[getLocale()][form][monthIndex] ?? String(monthIndex + 1)
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value)
}

function clock(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * SVR2 collection dates are date-only strings, which `new Date()` reads as UTC
 * midnight — local getters then shift them a day back west of Greenwich. Parse
 * the parts into a local date so the day and month never drift.
 */
export function parseCollectionDate(value: Date | string | number | null | undefined): Date | null {
  if (value == null) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim())
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  }
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** "16. júlí 2026" */
export function formatDateLong(value: Date | string | number): string {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const locale = getLocale()
  const month = MONTHS_LONG[locale][d.getMonth()]
  if (locale === 'is') return `${d.getDate()}. ${month} ${d.getFullYear()}`
  return `${d.getDate()} ${month} ${d.getFullYear()}`
}

/** "16. júl." */
export function formatDateShort(value: Date | string | number): string {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const locale = getLocale()
  const month = MONTHS_SHORT[locale][d.getMonth()]
  if (locale === 'is') return `${d.getDate()}. ${month}`
  return `${d.getDate()} ${month}`
}

/** "16. júl. 14:30" (24-hour clock) */
export function formatDateTimeShort(value: Date | string | number): string {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return `${formatDateShort(d)} ${clock(d)}`
}

export function formatDateTimeLong(value: Date | string | number): string {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return `${formatDateLong(d)} ${clock(d)}`
}

export function formatTime(value: Date | string | number): string {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return clock(d)
}
